/**
 * /operations/payroll/gl-output — Session 3B PR 15.
 * Owning agent: Rawatib. Generates D365 + SAP GL journal format.
 */

import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Download, FileText, Scale } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { GlJournalRunItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

type FormatPick = "d365" | "sap" | "csv";

function _fmtSar(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function GlJournalOutputPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [formatPick, setFormatPick] = useState<FormatPick>("d365");

  const list = useQuery({
    queryKey: ["session3b-gl-output", legalEntityId],
    queryFn: () => session3bApi.listGlOutputRuns(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: GlJournalRunItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const balanced = items.every((r) => r.balanced);
    return {
      total: items.length,
      attention: 0,
      critical: items.filter((r) => !r.balanced).length,
      allBalanced: balanced && items.length > 0,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("payroll.gl.title", "GL Journal Output")}
          subtitle={t(
            "payroll.gl.subtitle",
            "Generate D365 / SAP GL journal format. Downloadable for HR to import into the finance system. Live push: pending integration roadmap (post-M3X.GOV).",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-text-secondary">
              {t("payroll.gl.format_label", "Format")}:
            </span>
            {(["d365", "sap", "csv"] as FormatPick[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFormatPick(f)}
                className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
                  formatPick === f
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "payroll.gl.cta.generate_note",
                  `Generate GL Journal for current period · format=${formatPick.toUpperCase()}. Downloads the file.`,
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Download className="h-4 w-4" />
            {t("payroll.gl.cta.generate", "Generate GL Journal")}
          </button>
        </div>

        {counts.critical > 0 && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<Scale className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "payroll.gl.suggestion.unbalanced_headline",
              `${counts.critical} unbalanced journal${counts.critical === 1 ? "" : "s"} — debits ≠ credits.`,
            )}
            body={t(
              "payroll.gl.suggestion.unbalanced_body",
              "Rawatib notes: every payroll GL journal must balance. An unbalanced row signals a missing GL-account mapping for an earnings or deduction line item.",
            )}
            primaryAction={{
              label: t("payroll.gl.suggestion.open_mappings", "Open GL mappings"),
              onClick: () => window.location.assign("/settings/integrations"),
            }}
            suggestionKey="gl-unbalanced"
          />
        )}
        {counts.allBalanced && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "payroll.gl.suggestion.balanced_headline",
              `All ${items.length} GL journals balanced.`,
            )}
            body={t(
              "payroll.gl.suggestion.balanced_body",
              "Rawatib confirms: debits = credits across every posted period. Safe to import into D365 / SAP.",
            )}
            primaryAction={{
              label: t("payroll.gl.suggestion.open_history", "View Post History"),
              onClick: () => window.location.assign("/operations/payroll/post-history"),
            }}
            suggestionKey="gl-balanced"
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<FileText className="h-4 w-4" />}
            severity="info"
            headline={t(
              "payroll.gl.suggestion.empty_headline",
              "No GL journals generated yet.",
            )}
            body={t(
              "payroll.gl.suggestion.empty_body",
              "Rawatib notes: journals materialize when a payroll period is posted. POST the open period first.",
            )}
            primaryAction={{
              label: t("payroll.gl.suggestion.open_period_lock", "Open Period Lock"),
              onClick: () => window.location.assign("/operations/payroll/period-lock"),
            }}
            suggestionKey="gl-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("payroll.gl.col.period", "Period")}</th>
                  <th className="px-3 py-2">{t("payroll.gl.col.generated", "Generated")}</th>
                  <th className="px-3 py-2">{t("payroll.gl.col.format", "Format")}</th>
                  <th className="px-3 py-2">{t("payroll.gl.col.lines", "Lines")}</th>
                  <th className="px-3 py-2">{t("payroll.gl.col.debits", "Debits (SAR)")}</th>
                  <th className="px-3 py-2">{t("payroll.gl.col.credits", "Credits (SAR)")}</th>
                  <th className="px-3 py-2">{t("payroll.gl.col.balanced", "Balanced")}</th>
                  <th className="px-3 py-2">{t("payroll.gl.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.run_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2 text-text-secondary">{row.period_label}</td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.generated_at}</span>
                    </td>
                    <td className="px-3 py-2 text-xs uppercase text-text-secondary">{row.format}</td>
                    <td className="px-3 py-2 text-text-secondary">{row.line_count}</td>
                    <td className="px-3 py-2 font-mono text-xs">{_fmtSar(row.total_debits_sar)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{_fmtSar(row.total_credits_sar)}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "payroll.gl.trace.balanced",
                              `balanced = true when sum(debits) == sum(credits) to the cent. Required for every GL system. A false here means a missing account mapping for an earnings or deduction line item.`,
                            ),
                          )
                        }
                      >
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            row.balanced ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                          }`}
                        >
                          {row.balanced ? "yes" : "NO"}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() =>
                          showAlertTrace(
                            t(
                              "payroll.gl.action.download_note",
                              `Download GL journal in ${formatPick.toUpperCase()} format.`,
                            ),
                          )
                        }
                        className="inline-flex items-center gap-1 rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] opacity-50 transition-opacity group-hover:opacity-100 hover:bg-brand-blue/10"
                      >
                        <Download className="h-3 w-3" />
                        {t("payroll.gl.action.download", "Download")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="rounded-2xl border border-dashed border-border-subtle bg-surface-sunken/40 p-3 text-[10px] text-text-tertiary">
          {t(
            "payroll.gl.gov_note",
            "Note: live D365 / SAP push lands in the post-M3X.GOV integration sprint. Today every GL journal is downloaded by HR + imported manually.",
          )}
        </div>
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: counts.total,
            attention: 0,
            critical: counts.critical,
            top_kind: t("payroll.gl.title", "GL Journal Output"),
            window_label: counts.allBalanced
              ? t("payroll.gl.window_label_balanced", "all balanced")
              : `${counts.critical} unbalanced`,
          }}
        />
      </aside>
    </div>
  );
}
